import {
  InvoiceRecipientType,
  InvoiceSpedStatusRegistered,
  InvoiceType,
} from '../../invoice/invoices/invoices.enums';

export interface SpedDocumentListRequest {
  companyId?: number;
  status?: SpedDocumentStatus;
  afterCursor?: string;
  size?: number;
}

export interface SpedDocumentListResponse {
  items: SpedDocument[];
  nextCursor: string;
  hasNext: boolean;
}

export interface SpedDocument {
  id: number;
  companyId: number;
  companyName: string;
  assessmentYear: string;
  assessmentMonth: string;
  type: SpedDocumentType;
  invoiceIds: number[];
  purchaseIds: number[];
  status: SpedDocumentStatus;
  assessmentId: string;
  plugStorageSpedId: string;
  filePath: string;
}

export enum SpedDocumentType {
  STATE_TAX = 'STATE_TAX',
  FEDERAL_CONTRIBUTIONS = 'FEDERAL_CONTRIBUTIONS',
}

export enum SpedDocumentStatus {
  AWAITING_SELECTION = 'AWAITING_SELECTION',
  ASSESSING = 'ASSESSING',
  ASSESSED = 'ASSESSED',
  REJECTED = 'REJECTED',
  GENERATING_FILE = 'GENERATING_FILE',
  COMPLETED = 'COMPLETED',
  REOPENING = 'REOPENING',
  REOPENED = 'REOPENED',
  REPROCESSING = 'REPROCESSING',
}

export const SpedDocumentStatusLabel: Record<SpedDocumentStatus, string> = {
  ASSESSED: 'Apurado',
  ASSESSING: 'Apurando',
  AWAITING_SELECTION: 'Aguardando seleção',
  COMPLETED: 'Concluído',
  GENERATING_FILE: 'Gerando arquivo',
  REJECTED: 'Rejeitado',
  REOPENING: 'Reabrindo',
  REOPENED: 'Reaberto',
  REPROCESSING: 'Reprocessado',
};

export const SpedDocumentStatusColor: Record<SpedDocumentStatus, string> = {
  ASSESSED: 'green',
  ASSESSING: 'yellow',
  AWAITING_SELECTION: 'default',
  COMPLETED: 'green',
  GENERATING_FILE: 'yellow',
  REJECTED: 'red',
  REOPENING: 'blue',
  REOPENED: 'default',
  REPROCESSING: 'yellow',
};

export type SpedDocumentAssessmentMonth =
  | '01'
  | '02'
  | '03'
  | '04'
  | '05'
  | '06'
  | '07'
  | '08'
  | '09'
  | '10'
  | '11'
  | '12';

export interface SpedDocumentCreateRequest {
  companyId: number;
  assessmentYear: string;
  assessmentMonth: SpedDocumentAssessmentMonth;
}

export interface SpedDocumentEditRequest {
  spedDocumentId: number;
  companyId: number;
  assessmentYear: string;
  assessmentMonth: SpedDocumentAssessmentMonth;
}

export interface SpedDocumentManageImports {
  importType: 'PURCHASE' | 'INVOICE' | 'REMOVE' | 'INUTILIZATION';
  spedDocumentId: number;
  items: {
    entityId: number;
    entityEnum?: 'PURCHASE' | 'INVOICE' | 'INUTILIZATION';
  }[];
}

export type SpedDocumentInvoiceRecipientType = InvoiceRecipientType;

export const SpedDocumentInvoiceRecipientTypeLabel: Record<
  SpedDocumentInvoiceRecipientType,
  string
> = {
  CUSTOMER: 'Cliente',
  SUPPLIER: 'Fornecedor',
  INSURER: 'Seguradora',
};

export type SpedDocumentInvoiceType = Exclude<InvoiceType, 'CFE'>;

export const SpedDocumentInvoiceTypeColor: Record<
  SpedDocumentInvoiceType,
  string
> = {
  NFE: 'blue',
  NFC: 'green',
  NFS: 'violet',
};

export interface SpedDocumentInvoice {
  invoiceId: number;
  number: string;
  key: string;
  recipientType: SpedDocumentInvoiceRecipientType;
  recipientName: string;
  invoiceType: SpedDocumentInvoiceType;
  statusRegisteredInPlugStorage: InvoiceSpedStatusRegistered;
}

export interface SpedDocumentInvoiceListRequest {
  spedDocumentId: number;
  number?: string;
  recipientType?: SpedDocumentInvoiceRecipientType;
  invoiceTypes?: SpedDocumentInvoiceType[];
}

export interface SpedDocumentPurchaseListRequest {
  spedDocumentId: number;
  key?: string;
  documentNumber?: string;
  code?: number;
  supplierName?: string;
}

export interface SpedDocumentPurchase {
  id: number;
  code: number;
  key: string;
  documentNumber: string;
  supplierName: string;
  statusRegisteredInPlugStorage: InvoiceSpedStatusRegistered;
}

export interface SpedDocumentInutilizationListRequest {
  spedDocumentId: number;
  serie?: string;
  startNumber?: string;
  endNumber?: string;
  model?: string;
  justification?: string;
}

export interface SpedDocumentInutilization {
  id: number;
  companyName: string;
  serie: string;
  startNumber: number;
  endNumber: number;
  model: string;
  justification: string;
  status: InvoiceSpedStatusRegistered;
}
